export const path = {
  root: () => `/`,
  login: {
    entry: () => `/login`,
  },
  dashboard: () => `/dashboard`,
  ideas: {
    entry: () => `/ideas`,
  },
  clients: {
    entry: () => `/client`,
    details: (clientID: string) => `/client/${clientID}`,
    edit: (clientID: string) => `/client/edit/${clientID}`,
    accountDetail: (clientID: string, accountID: string) =>
      `/client/${clientID}/account/edit/${accountID}`,
    bonus111Detail: (clientID: string, accountID: string) =>
      `/client/${clientID}/bonus111/edit/${accountID}`,
    walletDetail: (clientID: string, accountID: string) =>
      `/client/${clientID}/wallet/edit/${accountID}`,
  },
  users: {
    entry: () => `/user`,
    details: (userID: string) => `/user/${userID}`,
    edit: (userID: string) => `/user/edit/${userID}`,
    create: () => `/user/create`,
  },
  groups: {
    entry: () => `/groups`,
    edit: (groupID: string) => `/groups/edit/${groupID}`,
    create: () => `/groups/create`,
  },
  permissions: {
    entry: () => `/permissions`,
    create: () => `/permissions/create`,
    edit: (permissionID: string) => `/permission/edit/${permissionID}`,
  },
  roles: {
    entry: () => `/role`,
    details: (roleID: string) => `/role/${roleID}`,
    edit: (roleID: string) => `/role/edit/${roleID}`,
  },
  verificationsV2: {
    entry: () => `/verificationsV2`,
    flow: () => `/verificationsV2/flow`,
    details: (verificationID: string) => `/verificationsV2/${verificationID}`,
    edit: (verificationID: string) => `/verificationsV2/edit/${verificationID}`,
    workReport: () => `/verificationsV2/work-report`,
  },
  cardVerifications: {
    entry: () => `/card-verifications`,
    details: (cardID: string) => `/card-verifications/${cardID}`,
    edit: (cardID: string) => `/card-verifications/edit/${cardID}`,
  },
  billing: {
    gateway: {
      entry: () => `/gateway`,
      details: (gateway: string) => `/gateway/${gateway}`,
      edit: (gateway: string) => `/gateway/edit/${gateway}`,
      delete: (gateway: string) => `/gateway/edit/${gateway}`,
      create: () => `/gateway/create`,
    },
    paymentGroup: {
      entry: () => `/withdrawal_group`,
      details: (group: string) => `/withdrawal_group/${group}`,
      edit: (group: string) => `/withdrawal_group/edit/${group}`,
      delete: (group: string) => `/withdrawal_group/edit/${group}`,
      create: () => `/withdrawal_group/create`,
    },
    transactions: {
      entry: () => `/transaction`,
      details: (transactionID: string) => `/transaction/${transactionID}`,
      edit: (transactionID: string) => `/transaction/edit/${transactionID}`,
    },
    deposit: {
      entry: () => `/deposit`,
      details: (depositID: string) => `/deposit/${depositID}`,
      edit: (depositID: string) => `/deposit/edit/${depositID}`,
    },
    withdrawal: {
      entry: () => `/withdrawal`,
      details: (withdrawalID: string) => `/withdrawal/${withdrawalID}`,
      edit: (withdrawalID: string) => `/withdrawal/edit/${withdrawalID}`,
    },
    internalTransfer: {
      entry: () => `/internal-transfer`,
      details: (internalTransferID: string) =>
        `/internal-transfer/${internalTransferID}`,
      edit: (internalTransferID: string) =>
        `/internal-transfer/edit/${internalTransferID}`,
    },
    balanceOperation: {
      entry: () => `/balance-operation`,
      create: () => `/balance-operation/create`,
    },
    exchangerTransfer: {
      entry: () => `/exchanger-transfer`,
      details: (exchangerTransferID: string) =>
        `/exchanger-transfer/${exchangerTransferID}`,
    },
    chargeback: {
      entry: () => `/chargeback`,
      create: () => `/chargeback/create`,
      edit: (chargebackID: string) => `/chargeback/edit/${chargebackID}`,
    },
    paymentTrafficMonitoring: {
      entry: (query?: { name: string; value: string }) => {
        if (!query?.value) return '/paymentTrafficMonitoring'

        const searchParams = new URLSearchParams({
          [query.name]: query.value,
        })

        return `/paymentTrafficMonitoring?${searchParams.toString()}`
      },
    },
  },
  finSystem: {
    allTransactions: {
      entry: () => `/all-transactions`,
    },
    debt: {
      entry: () => `/debt`,
    },
    wallets: {
      entry: () => `/wallets`,
      details: (walletID: string) => `/wallets/${walletID}`,
      inProgressDetails: () => `/wallet-in-progress`,
    },
    revision: {
      entry: () => '/revision',
      details: (walletID: string, currency: string) =>
        `/wallet/${walletID}/${currency}/revision/`,
    },
    budgetItem: {
      entry: () => '/budget-item',
      create: () => `/budget-item/create`,
      edit: (budgetItemID: string) => `/budget-item/edit/${budgetItemID}`,
    },
    sections: {
      entry: () => '/sections',
      create: () => `/sections/create`,
      edit: (sectionID: string) => `/sections/edit/${sectionID}`,
    },
    balanceSummary: {
      entry: () => '/balance-summary',
    },
  },
  walletGuard: {
    dashboard: () => '/wallet-guard/dashboard',
    alerts: () => '/wallet-guard/alerts',
    check: () => '/wallet-guard/check',
    wallets: () => '/wallet-guard/wallets',
    walletDetails: (walletID: string) => `/wallet-guard/wallets/${walletID}`,
    walletEdit: (walletID: string) => `/wallet-guard/wallets/${walletID}/edit`,
  },
  finDepartment: {
    kpi: {
      entry: () => `/fin-department/kpi`,
    },
    quarterlyKpi: {
      entry: () => `/fin-department/quarterly-kpi`,
    },
    processingTransactions: {
      entry: () => `/fin-department/processing-transactions`,
    },
    cardVerification: {
      entry: () => '/fin-department/card-verification',
    },
    settings: {
      entry: () => '/fin-department/settings',
    },
  },
  ib: {
    cashbooster: {
      entry: () => `/IB/cashbooster`,
    },
    partners: {
      entry: () => `/IB/partner`,
      details: (partnerID: string) => `/IB/partner/${partnerID}`,
      edit: (partnerID: string) => `/IB/partner/edit/${partnerID}`,
      attach: () => `/IB/partner/attach`,
    },
    ad: {
      socialMedia: {
        entry: () => `/IB/AD/social`,
        create: () => `/IB/AD/social/create`,
        edit: (socialMediaID: string) => `/IB/AD/social/edit/${socialMediaID}`,
      },
      landing: {
        entry: () => `/IB/AD/landing`,
        edit: (landingID: string) => `/IB/AD/landing/edit/${landingID}`,
        create: () => `/IB/AD/landing/create`,
      },
      website_pages: {
        entry: () => `/IB/AD/website_pages`,
        edit: (webPageSiteID: string) =>
          `/IB/AD/website_pages/edit/${webPageSiteID}`,
        create: () => `/IB/AD/website_pages/create`,
      },
      offers: {
        entry: () => `/IB/AD/offers`,
        create: () => `/IB/AD/offers/create`,
        edit: (offerID: string) => `/IB/AD/offers/edit/${offerID}`,
      },
      banners: {
        entry: () => `/IB/AD/banners`,
        create: () => `/IB/AD/banners/create`,
        edit: (websiteBannerID: string) =>
          `/IB/AD/banners/edit/${websiteBannerID}`,
      },
    },
  },
  kpi: {
    entry: () => `/kpi`,
    details: () => `/kpi-by-day`,
    transactions: () => `/transaction-by-day`,
  },
  forbiddenDomain: {
    entry: () => `/forbidden-domain`,
    create: () => `/forbidden-domain/create`,
  },
  nova: {
    orders: {
      entry: () => `/nova-orders`,
    },
    dividendEvents: {
      entry: () => `/dividend-events`,
      details: (dividendEventID: string) =>
        `/dividend-event/${dividendEventID}`,
      edit: (dividendEventID: string) =>
        `/dividend-event/edit/${dividendEventID}`,
    },
    ownerWallets: {
      entry: () => `/nova-owner-wallets`,
    },
    sellOffers: {
      entry: () => `/sell-offers`,
    },
    sellPayouts: {
      entry: () => `/sell-payouts`,
    },
    hotBuyOrders: {
      entry: () => `/hot-buy-orders`,
    },
    instalments: {
      entry: (ID: string | null) =>
        ID ? `/instalments?id=${ID}` : '/instalments',
    },
    properties: {
      entry: () => `/nova-properties`,
      detail: () => '/nova-properties/details',
    },
  },
  giftShop: {
    category: {
      entry: () => `/category`,
      details: (categoryID: string) => `/category/${categoryID}`,
      edit: (categoryID: string) => `/category/edit/${categoryID}`,
      create: () => '/category/details',
    },
    gifts: {
      entry: () => `/gifts`,
      details: (giftID: string) => `/gifts/${giftID}`,
      edit: (giftID: string) => `/gifts/edit/${giftID}`,
      create: () => '/gifts/create',
    },
    giftOrders: {
      entry: () => `/gift-orders`,
      details: (giftOrderID: string) => `/gift-orders/${giftOrderID}`,
      edit: (giftOrderID: string) => `/gift-orders/edit/${giftOrderID}`,
    },
  },
  copytrade: {
    strategies: {
      entry: () => `/strategies`,
      details: (strategyID: string) => `/strategies/${strategyID}`,
      edit: (strategyID: string) => `/strategies/edit/${strategyID}`,
    },
    achievements: {
      entry: () => `/achievements`,
      create: () => '/achievements/create',
      edit: (achievementID: string) => `/achievements/edit/${achievementID}`,
    },
    assessment: {
      entry: () => `/assessment`,
    },
  },
  promo: {
    anniversaryContest: {
      traders: {
        entry: () => `/anniversaryContest/traders`,
      },
      partners: {
        entry: () => `/anniversaryContest/partners`,
      },
    },
    demoContest: {
      entry: () => `/demoContest`,
      create: () => '/demoContestClient/create',
      details: (demoContestID: string) => `/demoContest/${demoContestID}`,
      edit: (demoContestID: string) => `/demoContest/edit/${demoContestID}`,
    },
    ramadan2025: {
      competitions: {
        entry: () => `/ramadan2025/competitions`,
        details: (competitionID: string) =>
          `/ramadan2025/details/${competitionID}`,
      },
      participants: {
        entry: () => `/ramadan2025/participants`,
      },
    },
    ramadan2026: {
      competitions: {
        entry: () => `/ramadan2026/competitions`,
        details: (competitionID: string) =>
          `/ramadan2026/details/${competitionID}`,
      },
      participants: {
        entry: () => `/ramadan2026/participants`,
      },
    },

    goldenMay2025: {
      participants: {
        entry: () => `/goldenMay2025/participants`,
      },
      tasks: {
        entry: () => `/goldenMay2025/tasks`,
        details: (taskID: string | number) => `/goldenMay2025/tasks/${taskID}`,
      },
      winners: {
        entry: () => `/goldenMay2025/winners`,
      },
    },
    goldenMay2026: {
      participants: {
        entry: () => `/goldenMay2026/participants`,
      },
      tasks: {
        entry: () => `/goldenMay2026/tasks`,
        details: (taskID: string | number) => `/goldenMay2026/tasks/${taskID}`,
      },
      winners: {
        entry: () => `/goldenMay2026/winners`,
      },
      tickets: {
        entry: () => `/goldenMay2026/tickets`,
      },
    },
    worldCup2026: {
      participants: {
        entry: () => `/worldCup2026/participants`,
      },
      tasks: {
        entry: () => `/worldCup2026/tasks`,
        details: (taskID: string | number) => `/newYear2026/tasks/${taskID}`,
      },
      winners: {
        entry: () => `/worldCup2026/winners`,
      },
      tickets: {
        entry: () => `/worldCup2026/tickets`,
      },
      players: {
        entry: () => `/worldCup2026/players`,
      },
    },
    partnerContest2026: {
      participants: {
        entry: () => `/IB/partner-contest-2026`,
      },
    },
    newYear2026: {
      participants: {
        entry: () => `/newYear2026/participants`,
      },
      tasks: {
        entry: () => `/newYear2026/tasks`,
        details: (taskID: string | number) => `/newYear2026/tasks/${taskID}`,
      },
      winners: {
        entry: () => `/newYear2026/winners`,
      },
      tickets: {
        entry: () => `/newYear2026/tickets`,
      },
    },
    bonus150: {
      participants: {
        entry: () => `/bonus150/participants`,
      },
      settings: {
        entry: () => `/bonus150/settings`,
      },
    },
  },
  exchangers: {
    entry: () => `/exchangers`,
    create: () => '/exchangers/create',
    edit: (exchangerID: string) => `/exchangers/edit/${exchangerID}`,
  },
  depositBonus: {
    promoCodes: {
      entry: () => `/depositBonus/promoCodes`,
      create: () => '/depositBonus/promoCodes/create',
      edit: (promoCodeID: string) =>
        `/depositBonus/promoCodes/edit/${promoCodeID}`,
    },
    promoCodeUsage: {
      entry: () => `/depositBonus/promoCodeUsage`,
    },
    settings: {
      entry: () => `/depositBonus/settings`,
    },
  },
  translations: {
    services: {
      entry: () => `/translations/services`,
      edit: (serviceName: string, lang: string) =>
        `/translations/services/edit/${serviceName}/${lang}`,
      details: (serviceName: string) =>
        `/translations/services/details/${serviceName}`,
    },
  },
  budget: {
    entry: () => '/budget',
    roles: {
      entry: () => '/budget/roles',
    },
    categories: {
      entry: () => '/budget/categories',
    },
    budgets: {
      entry: () => '/budget/budgets',
      create: (currentPeriodId?: number | null) => {
        if (!currentPeriodId) return `/budget/budgets/create`
        const params = new URLSearchParams({
          currentPeriodId: String(currentPeriodId),
        })
        return `/budget/budgets/create?${params.toString()}`
      },
      edit: (id: string) => `/budget/budgets/edit/${id}`,
      createPaymentRequest: (id: string) =>
        `/budget/budgets/paymentRequest/${id}`,
    },
    payments: {
      entry: () => '/budget/payments',
    },
  },
  report: {
    dividends: {
      entry: (dateFrom?: string, dateTo?: string, sorterField?: string) => {
        if (!dateFrom || !dateTo) return '/report/dividends'

        const params = sorterField
          ? new URLSearchParams({
              dateFrom,
              dateTo,
              sorterField,
            })
          : new URLSearchParams({
              dateFrom,
              dateTo,
            })

        return `/report/dividends?${params.toString()}`
      },
    },
    commissions: {
      entry: (dateFrom?: string, dateTo?: string, sorterField?: string) => {
        if (!dateFrom || !dateTo) return '/report/commissions'

        const params = sorterField
          ? new URLSearchParams({
              dateFrom,
              dateTo,
              sorterField,
            })
          : new URLSearchParams({
              dateFrom,
              dateTo,
            })

        return `/report/commissions?${params.toString()}`
      },
    },
    exDifference: {
      entry: (dateFrom?: string, dateTo?: string, sorterField?: string) => {
        if (!dateFrom || !dateTo) return '/report/ex_difference'

        const params = sorterField
          ? new URLSearchParams({
              dateFrom,
              dateTo,
              sorterField,
            })
          : new URLSearchParams({
              dateFrom,
              dateTo,
            })

        return `/report/ex_difference?${params.toString()}`
      },
    },
  },
  technical: () => `/technical`,
}
